import React from "react";
import { Users } from "lucide-react";
import { useListOwnerEmail } from "../../hooks/queries/useListOwnerEmail";
import { useRespondToInvitation } from "../../hooks/mutations/useRespondToInvitation";
import { invitationListLabel, invitationSenderLabel } from "../../lib/listSharing";
import { List, ListMember } from "../../types";

interface PendingInvitationProps {
  invite: ListMember;
  lists: List[];
  /** When set, also offer Decide later — the board prompt uses this. */
  onDefer?: (listId: string) => void;
}

/**
 * One unanswered Invitation: who sent it, that it is a list, the list's
 * name, and Accept / Decline. Settings uses this as a row. The board
 * prompt uses the same card in the corner, with Decide later.
 */
const PendingInvitation: React.FC<PendingInvitationProps> = ({
  invite,
  lists,
  onDefer,
}) => {
  const list = lists.find((l) => l.id === invite.list_id);
  const listLabel = invitationListLabel(list);
  const { data: ownerEmail } = useListOwnerEmail(invite.list_id, true);
  const who = invitationSenderLabel(ownerEmail);
  const respond = useRespondToInvitation();
  const pending = respond.isPending;

  return (
    <div
      role="region"
      aria-label={`Invitation to ${listLabel}`}
      className="px-4 py-3 bg-warm-bg-elevated border border-warm-border rounded-xl flex flex-wrap items-center justify-between gap-3 shadow-soft-md"
    >
      <span className="flex items-center gap-2 text-sm text-warm-text-primary min-w-0">
        <Users size={16} className="text-warm-blue flex-shrink-0" aria-hidden="true" />
        <span className="min-w-0 break-words">
          <strong className="font-medium">{who}</strong>
          {" "}
          shared a list with you —{" "}
          <strong className="font-medium">{listLabel}</strong>
        </span>
      </span>
      <span className="flex flex-wrap items-center gap-2 flex-shrink-0">
        <button
          type="button"
          onClick={() => respond.mutate({ listId: invite.list_id, accept: false })}
          aria-label={`Decline invitation to ${listLabel}`}
          disabled={pending}
          className="px-2.5 py-1 text-sm text-warm-text-secondary hover:text-warm-text-primary transition-colors disabled:opacity-50"
        >
          Decline
        </button>
        {onDefer && (
          <button
            type="button"
            onClick={() => onDefer(invite.list_id)}
            aria-label={`Decide later on invitation to ${listLabel}`}
            disabled={pending}
            className="px-2.5 py-1 text-sm text-warm-text-secondary hover:text-warm-text-primary transition-colors disabled:opacity-50"
          >
            Decide later
          </button>
        )}
        <button
          type="button"
          onClick={() => respond.mutate({ listId: invite.list_id, accept: true })}
          aria-label={`Accept invitation to ${listLabel}`}
          disabled={pending}
          className="px-2.5 py-1 bg-warm-blue hover:bg-warm-blue-dark text-white rounded-soft text-sm transition-colors disabled:opacity-50"
        >
          Accept
        </button>
      </span>
    </div>
  );
};

export default PendingInvitation;
